
<template>
  <div
      ref="dropdown"
      class="dropdown"
      :class="{ 'dropdown-active': isOpen }"
  >
    <button
        ref="dropdownButton"
        class="dropdown__button"
        :class="{ 'dropdown__button--active': isOpen, readonly: readonly }"
        type="button"
        :aria-expanded="isOpen.toString()"
        @click="toggleDropdown"
    >
      {{ buttonText }}
    </button>

    <Teleport v-if="!readonly" to="body">
      <div
          v-if="isMounted"
          ref="dropdownList"
          class="dropdown__list dropdown__list--teleported"
          :class="{ 'dropdown__list--visible': isOpen }"
          :style="dropdownStyles"
      >
        <ul ref="dropdownInner">
          <li
              v-for="option in options"
              :key="option.value"
              class="dropdown__list-item"
          >
            <label class="drop-label">
              <input
                  :checked="modelValue === option.value"
                  type="radio"
                  :name="name"
                  :value="option.value"
                  @change="selectValue(option.value)"
              >
              <span class="drop-name">{{ option.label }}</span>
            </label>
          </li>
        </ul>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { nextTick, Teleport } from 'vue';

export default {
  name: 'SingleSelectDropdown',
  components: {
    Teleport,
  },
  props: {
    modelValue: {
      type: [String, Number],
      default: null,
    },
    options: {
      type: Array,
      default: () => [],
    },
    label: {
      type: String,
      required: true,
    },
    placeholder: {
      type: String,
      default: 'Выберите',
    },
    name: {
      type: String,
      default: 'dropdown',
    },
    readonly: false
  },
  emits: ['update:modelValue'],
  data: () => ({
    isOpen: false,
    isMounted: false,
    dropdownStyles: {},
  }),
  computed: {
    buttonText() {
      if (!this.modelValue) {
        return this.placeholder;
      }

      const selected = this.options.find(item => item.value === this.modelValue);
      return selected ? selected.label : this.placeholder;
    },
  },
  mounted() {
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.updatePosition = this.updatePosition.bind(this);
    document.addEventListener('click', this.onDocumentClick);
    window.addEventListener('scroll', this.updatePosition, true);
    window.addEventListener('resize', this.updatePosition);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onDocumentClick);
    window.removeEventListener('scroll', this.updatePosition, true);
    window.removeEventListener('resize', this.updatePosition);
  },
  methods: {
    async toggleDropdown() {
      if (this.readonly) return;
      if (!this.isOpen) {
        // Открываем
        this.isMounted = true;
        await nextTick();

        // Сначала позиционируем с высотой 0
        this.updatePosition(false);

        await nextTick();

        // Потом открываем с полной высотой
        this.isOpen = true;
        this.updatePosition(true);
      } else {
        // Закрываем
        this.isOpen = false;
        this.updatePosition(false);

        setTimeout(() => {
          this.isMounted = false;
        }, 300);
      }
    },
    updatePosition(withHeight = true) {
      if (!this.$refs.dropdownButton) return;

      const button = this.$refs.dropdownButton;
      const rect = button.getBoundingClientRect();

      let maxHeight = '0px';
      let overflowY = 'hidden';

      if (withHeight && this.isMounted) {
        const inner = this.$refs.dropdownInner;
        if (inner) {
          let contentHeight = inner.scrollHeight;
          if (contentHeight > 200) {
            maxHeight = '200px';
            overflowY = 'auto';
          } else {
            contentHeight = contentHeight + 20;
            maxHeight = `${contentHeight}px`;
            overflowY = 'hidden';
          }
        }
      }

      this.dropdownStyles = {
        position: 'absolute',
        top: `${rect.bottom + window.scrollY}px`,
        left: `${rect.left + window.scrollX}px`,
        width: `${rect.width}px`,
        zIndex: 9999,
        maxHeight: maxHeight,
        overflowY: overflowY,
        transition: 'max-height 0.3s ease',
      };
    },
    async selectValue(value) {
      this.$emit('update:modelValue', value);
      this.isOpen = false;
      this.updatePosition(false);

      setTimeout(() => {
        this.isMounted = false;
      }, 300);
    },
    onDocumentClick(e) {
      const dropdown = this.$refs.dropdown;
      const list = this.$refs.dropdownList;

      if (!dropdown) return;

      if (!dropdown.contains(e.target) && (!list || !list.contains(e.target))) {
        if (this.isOpen) {
          this.isOpen = false;
          this.updatePosition(false);

          setTimeout(() => {
            this.isMounted = false;
          }, 300);
        }
      }
    },
  },
};
</script>

<style scoped>
.dropdown__list--teleported {
  overflow: hidden;
  box-sizing: border-box;

}
.dropdown__list.dropdown__list--teleported ul { height: auto;}
</style>